package info.rezident.app.pages.recommendations

import info.rezident.app.data.quiz.RecommendationItem
import kotlinx.serialization.Serializable

/**
 * Contract for the Recommendations feature containing State, Actions, and Effects
 */

/**
 * Data class representing user feedback for recommendations
 */
@Serializable
data class RecommendationsFeedback(
    val usefulScore: Int? = null,
    val expectationScore: Int? = null,
    val comment: String? = null
)

/**
 * Full-page blocking errors that prevent showing recommendations
 */
sealed class RecommendationsPageError {
    object OldQuizVersion : RecommendationsPageError()
    object QuizNotFound : RecommendationsPageError()
    object RecommendationsNotAvailable : RecommendationsPageError()
}

/**
 * Localized AI generation error with timestamp for cooldown logic
 */
data class ProsConsGenerationError(
    val occurredAt: Long, // timestamp when error occurred
    val message: String = "Mai durează puțin. Te rugăm să reîncerci curând."
)

/**
 * Localized payment/checkout error
 */
data class PaymentError(
    val message: String
)

/**
 * Immutable state representing the complete UI state of the Recommendations page
 */
data class RecommendationsState(
    val isLoading: Boolean = true,
    // Unified error handling
    val pageError: RecommendationsPageError? = null, // Full-page blocking errors
    val paymentError: PaymentError? = null, // Localized payment error (shown inline)
    // Recommendations data
    val recommendations: List<RecommendationItem> = emptyList(),
    val incompatibleRecommendations: List<RecommendationItem> = emptyList(),
    val isCreatingCheckout: Boolean = false,
    val isUnlocked: Boolean = false,
    val isAnonymousUser: Boolean = false,
    val isNotSignedIn: Boolean = false,
    val showCheckoutDialog: Boolean = false,
    val showAccountLinking: Boolean = false,
    val selectedPriceId: String? = null,
    val selectedPriceText: String? = null,
    val selectedPriceInitialText: String? = null,
    val selectedPriceDiscountText: String? = null,
    val hasAnswers: Boolean = false,
    val feedback: RecommendationsFeedback = RecommendationsFeedback(),
    val isSavingFeedback: Boolean = false,
    val isGeneratingProsCons: Boolean = false,
    val prosConsGenerationError: ProsConsGenerationError? = null, // Localized AI error (shown inline)
    // A/B test (Remote Config `recommendations_sorted`): true = A (ranked, previous behavior),
    // false = B (ranking hidden), null until the variant is resolved
    val recommendationsSorted: Boolean? = null,
    // Ranks in the order the cards are displayed: by rank in variant A, random but stable per quiz in variant B
    val recommendationOrder: List<Int> = emptyList(),
    // Ranks of the cards shown as locked while the quiz is locked. New quizzes: ranks 1, 2 + 2 random from 4-8
    // in variant A, 3 random cards (max 2 of the top 3 matches) in variant B. Saved ranks are kept as they are.
    // Never the first displayed card.
    val lockedRecommendationRanks: List<Int> = emptyList()
)

/**
 * One-time effects that should be handled by the UI
 */
sealed class RecommendationsEffect {
    data class NavigateTo(val url: String) : RecommendationsEffect()
    data class ScrollToHash(val hash: String) : RecommendationsEffect()
    data class ShowError(val message: String) : RecommendationsEffect()
    data object ShowConfetti : RecommendationsEffect()
}